import styled from "@emotion/styled/macro";
import { type UseFormReturn } from "react-hook-form";
import { Contact } from "@sunrun/experience-ui-components/lib/components/ContactCard";
import { useMemo } from "react";
import { UtilityBillPastDueBalanceBanner } from "@sunrun/experience-ui-components";
import { type SplatUtilityResponse } from "../../../amplify/backend/function/OfferExpUsage/ts/public/types";
import { type ContractReviewMessage } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { UtilityInfo } from "./UtilityInfo";
import { UsageInfo } from "./UsageInfo";
import { DocumentUpload } from "./DocumentUpload";
import { UsageSlideoutContacts } from "./UsageSlideoutContacts";
import { VoidProposalsConfirmationModal } from "components/molecules/VoidProposalsConfirmationModal";
import { Attachment, UsageCadence, UtilityInfoState } from "utils/usageUtils";
import { UpdateContactWarningBanner } from "checkout/components/molecules/UpdateContactWarningBanner";
import { UsageType } from "constants/usageTypes";
import { hasSubmittedToContractReview } from "constants/prospect";
import { SplatProspectResponse } from "providers/Types";
import {
  ContractReviewCategory,
  getMessagesByCategory,
  hasMessage,
  isRateScheduleMismatchMessage,
} from "utils/contractReviewUtils";
import { ImmediateUploadConfig } from "hooks/useImmediateAttachmentUpload";

type UsageSlideoutContentProps = {
  utilityBillPastDueBalanceFlowIsEnabled: boolean;
  hasUploadedUtilityBillFileWithPastDueBalance: boolean;
  utilityPastDueBalance: number;
  utilityCompany: string;
  isSunrunManaged?: boolean;
  monthlyUsage: (number | undefined)[] | null;
  setMonthlyUsage: (arr: (number | undefined)[]) => void;
  showVoidProposalModal: boolean;
  setShowVoidProposalModal: (show: boolean) => void;
  handleSave: (voidProposals: boolean | undefined) => void;
  modifiedField: string | null;
  utilitiesOptions?: SplatUtilityResponse[];
  formUtils: UseFormReturn<UtilityInfoState>;
  usageCadence: UsageCadence;
  setUsageCadence: (cadence: UsageCadence) => void;
  usageType: UsageType;
  setUsageType: (type: UsageType) => void;
  homeSize?: number;
  setHomeSize?: (size: number) => void;
  attachments: Attachment[];
  immediateUpload?: ImmediateUploadConfig;
  selectedUtilityContact?: Contact;
  setSelectedUtilityContact: (contact: Contact) => void;
  refSetter?: (node: HTMLDivElement | null) => void;
  showUtilityBillWarningBanner: boolean;
  setShowUtilityBillWarningBanner: (show: boolean) => void;
  hasOffers?: boolean;
  refetchContacts: () => void;
  usage: SplatProspectResponse | undefined;
  hasSignedOffer?: boolean;
  onGetBillOnline?: () => void;
  intervalDataDeclined?: boolean;
  intervalDataDeclinedReason?: string;
  fetchingIntervalDataStatus?: boolean;
  setIntervalDataDeclinedReason?: (reason: string) => void;
  intervalDataUsed?: boolean;
  intervalDataInitialLoading?: boolean;
  intervalDataStatus?: string | null;
  additionalSystem?: boolean;
  contractReviewMessages?: ContractReviewMessage[];
  onDeleteUtilityBill?: (
    file: Attachment & { documentId: string }
  ) => Promise<void> | void;
};

const UsageSlideoutContent: React.FC<UsageSlideoutContentProps> = ({
  utilityBillPastDueBalanceFlowIsEnabled,
  hasUploadedUtilityBillFileWithPastDueBalance,
  utilityPastDueBalance,
  utilityCompany,
  additionalSystem,
  isSunrunManaged,
  monthlyUsage,
  setMonthlyUsage,
  showVoidProposalModal,
  setShowVoidProposalModal,
  handleSave,
  modifiedField,
  utilitiesOptions,
  formUtils,
  usageCadence,
  setUsageCadence,
  usageType,
  setUsageType,
  homeSize,
  setHomeSize,
  attachments,
  immediateUpload,
  selectedUtilityContact,
  setSelectedUtilityContact,
  refSetter,
  showUtilityBillWarningBanner,
  setShowUtilityBillWarningBanner,
  hasOffers,
  refetchContacts,
  usage,
  hasSignedOffer,
  onGetBillOnline,
  intervalDataDeclined,
  intervalDataDeclinedReason,
  fetchingIntervalDataStatus,
  setIntervalDataDeclinedReason,
  intervalDataUsed,
  intervalDataInitialLoading,
  intervalDataStatus,
  contractReviewMessages = [],
  onDeleteUtilityBill,
}) => {
  const isUtilityBillDeleteBlocked = hasSubmittedToContractReview(
    usage?.leadStage
  );

  const isContractReviewTextNeeded = useMemo(() => {
    if (!modifiedField || !contractReviewMessages?.length) return false;

    const contractReviewCategories = [
      "estimation_method_check",
    ] as ContractReviewCategory[];
    const filteredMessagesByCategory = getMessagesByCategory(
      contractReviewMessages,
      ...contractReviewCategories
    );
    const contractReviewVoidingFields = ["rateSchedule"];

    return (
      filteredMessagesByCategory.length > 0 &&
      contractReviewVoidingFields.includes(modifiedField ?? "")
    );
  }, [contractReviewMessages, modifiedField]);

  const customModalProps = useMemo(() => {
    if (!isContractReviewTextNeeded) return undefined;

    return {
      customPrimaryButtonText: "No, Go Back",
      customSecondaryButtonText: "Update",
      customBody:
        "Editing this information will require creating a new offer and you will lose your current checkout progress.",
    };
  }, [isContractReviewTextNeeded]);

  return (
    <StyledUsageSlideoutContent>
      {showUtilityBillWarningBanner && (
        <UpdateContactWarningBanner
          contactType="utility_bill"
          onClose={() => setShowUtilityBillWarningBanner(false)}
        />
      )}
      {showVoidProposalModal && modifiedField && (
        <VoidProposalsConfirmationModal
          onCancel={() => setShowVoidProposalModal(false)}
          onConfirm={() => handleSave(true)}
          modifiedField={modifiedField}
          {...customModalProps}
        />
      )}
      <UsageInfo
        additionalSystem={additionalSystem}
        isSunrunManaged={isSunrunManaged}
        monthlyUsageData={monthlyUsage}
        setMonthlyUsageData={setMonthlyUsage}
        usageCadence={usageCadence}
        setUsageCadence={setUsageCadence}
        usageType={usageType}
        setUsageType={setUsageType}
        homeSize={homeSize}
        setHomeSize={setHomeSize}
        usage={usage}
        getBillOnline={onGetBillOnline}
        intervalDataDeclined={intervalDataDeclined}
        intervalDataDeclinedReason={intervalDataDeclinedReason}
        fetchingIntervalDataStatus={fetchingIntervalDataStatus}
        setIntervalDataDeclinedReason={setIntervalDataDeclinedReason}
        intervalDataUsed={intervalDataUsed}
        intervalDataInitialLoading={intervalDataInitialLoading}
        intervalDataStatus={intervalDataStatus}
        contractReviewMessages={contractReviewMessages}
      />
      <DocumentUpload
        uploadedFiles={attachments}
        immediateUpload={immediateUpload}
        documentType="Utility Bill"
        description="Upload monthly utility bill(s) for review."
        title="Utility Bills"
        getBillOnline={onGetBillOnline}
        intervalDataInitialLoading={intervalDataInitialLoading}
        intervalDataStatus={intervalDataStatus}
        markUploadDirty={false}
        bannerContent={
          utilityBillPastDueBalanceFlowIsEnabled &&
          hasUploadedUtilityBillFileWithPastDueBalance && (
            <StyledUtilityBillPastDueBalanceBanner
              utilityCompany={utilityCompany ?? ""}
              utilityPastDueBalance={utilityPastDueBalance ?? 0}
            />
          )
        }
        onDeleteUploadedFile={onDeleteUtilityBill}
        isDeleteBlocked={isUtilityBillDeleteBlocked}
      />
      <UtilityInfo
        utilitiesOptions={utilitiesOptions}
        formUtils={formUtils}
        contractReviewMessages={
          hasMessage(contractReviewMessages, isRateScheduleMismatchMessage)
            ? contractReviewMessages
            : undefined
        }
      />
      <UsageSlideoutContacts
        selectedUtilityContact={selectedUtilityContact}
        setSelectedUtilityContact={setSelectedUtilityContact}
        refSetter={refSetter}
        hasOffers={hasOffers}
        refetchContacts={refetchContacts}
        hasSignedOffer={hasSignedOffer}
      />
    </StyledUsageSlideoutContent>
  );
};

const StyledUsageSlideoutContent = styled.div`
  display: grid;
  grid-row-gap: 32px;
  margin-bottom: 32px;

  @media (max-width: 1100px) {
    width: 100%;
  }

  @media (max-width: 768px) {
    margin-top: 32px;
  }
`;

const StyledUtilityBillPastDueBalanceBanner = styled(
  UtilityBillPastDueBalanceBanner
)`
  margin-bottom: 24px;
`;

export { UsageSlideoutContent };
export type { UsageSlideoutContentProps };
